import React, { useState } from 'react';
import { quickNotes } from '../data/quickNotes';
import { ChevronDown, FileText, Code, Tag, BookOpen, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const QuickNotesSection = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredNotes = quickNotes.filter(note =>
    note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
    note.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <section
      id="notes"
      className="min-h-screen flex items-center justify-center py-20 relative overflow-hidden"
      style={{ backgroundColor: '#020617' }}
    >
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-6 max-w-5xl relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Quick <span className="text-cyan-400">Notes</span>
          </h2>
          <div className="flex justify-center mb-4">
            <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full"></div>
          </div>
          <p className="text-gray-400 text-lg mb-8">
            Quick reference for Cloud & DevOps essentials
          </p>

          {/* Search Bar */}
          <motion.div
            className="max-w-md mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="relative">
              <input
                type="text"
                placeholder="Search notes by topic, tag, or category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-6 py-3 bg-gray-900/50 border border-gray-700/50 rounded-full text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/50 focus:ring-2 focus:ring-cyan-400/20 transition-all duration-300"
              />
              <BookOpen className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
            </div>
          </motion.div>
        </motion.div>

        {/* Notes Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <Accordion
            type="single"
            collapsible
            className="space-y-6"
          >
            {filteredNotes.map((note, index) => (
              <AccordionItem
                key={note.id}
                value={note.id}
                className="border-none"
              >
                <motion.div
                  className="rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/10"
                  style={{
                    backgroundColor: '#020f25',
                    border: '1px solid rgba(6, 182, 212, 0.2)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{
                    borderColor: 'rgba(6, 182, 212, 0.4)',
                    boxShadow: '0 8px 30px rgba(6, 182, 212, 0.15)'
                  }}
                >
                  <AccordionTrigger
                    className="px-6 py-5 hover:no-underline hover:bg-gray-800/30 transition-colors duration-200 group"
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-4 text-left">
                        <span className="text-3xl">{note.icon}</span>
                        <div>
                          <h3 className="text-xl font-bold text-white group-hover:text-cyan-400 transition-colors">
                            {note.title}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">{note.category}</p>
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="px-6 pb-6 pt-2">
                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {note.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-full text-xs font-medium"
                        >
                          <Tag size={12} />
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Description */}
                    <p className="text-gray-300 leading-relaxed mb-4">
                      {note.description}
                    </p>

                    {/* Key Points */}
                    <div className="mb-5">
                      <h4 className="text-cyan-400 font-semibold mb-3 flex items-center gap-2">
                        <BookOpen size={16} />
                        Key Points
                      </h4>
                      <ul className="space-y-2">
                        {note.keyPoints.map((point, idx) => (
                          <li key={idx} className="text-gray-400 text-sm flex items-start gap-2">
                            <span className="text-cyan-400 mt-1">•</span>
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Code Example */}
                    {note.codeExample && (
                      <div className="mb-4">
                        <h4 className="text-cyan-400 font-semibold mb-3 flex items-center gap-2">
                          <Code size={16} />
                          {note.codeExample.title}
                        </h4>
                        <div className="bg-gray-900/80 border border-gray-700/50 rounded-lg overflow-hidden">
                          <div className="px-4 py-2 bg-gray-800/50 border-b border-gray-700/50 flex items-center justify-between">
                            <span className="text-xs text-gray-400 font-mono">{note.codeExample.language}</span>
                          </div>
                          <pre className="p-4 overflow-x-auto">
                            <code className="text-sm text-gray-300 font-mono leading-relaxed">
                              {note.codeExample.code}
                            </code>
                          </pre>
                        </div>
                      </div>
                    )}

                    {/* Detailed Notes Link */}
                    {note.hasDetailedNotes && note.notesLink && (
                      <a
                        href={note.notesLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 text-sm font-medium transition-colors duration-300 hover:underline mt-2"
                      >
                        <FileText size={14} />
                        View detailed {note.title} notes
                        <ExternalLink size={12} />
                      </a>
                    )}
                  </AccordionContent>
                </motion.div>
              </AccordionItem>
            ))}
          </Accordion>

          {/* No Results Message */}
          {filteredNotes.length === 0 && (
            <motion.div
              className="text-center py-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <p className="text-gray-400 text-lg">No notes found matching "{searchTerm}"</p>
              <button
                onClick={() => setSearchTerm('')}
                className="mt-4 text-cyan-400 hover:text-cyan-300 transition-colors"
              >
                Clear search
              </button>
            </motion.div>
          )}
        </motion.div>

        {/* Footer Note */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <p className="text-gray-500 text-sm">
            💡 Click on any note to expand and view code examples
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default QuickNotesSection;
